import React, { useState } from 'react';
import validator from 'validator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import InputField from '@components/ui/InputField';
import Textarea from '@components/ui/Textarea';
import Button from '@components/ui/Button';
import { toast } from 'react-toastify';
import 'react-phone-input-2/lib/style.css';
import PhoneInput from 'react-phone-input-2';
import { getNames } from 'country-list';

const DemoForm = () => {
  const countries = getNames().filter((country) => country !== 'Israel');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    country: '',
    countryCode: '',
    phone: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const fullPhone =
      formData.phone && formData.phone.startsWith('+')
        ? formData.phone
        : `+${formData.phone || ''}`;
    const parsedPhone = parsePhoneNumberFromString(fullPhone);

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validator.isEmail(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.company.trim()) newErrors.company = 'Company name is required';
    if (!formData.country.trim()) newErrors.country = 'Country is required';

    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!parsedPhone || !parsedPhone.isValid()) {
      newErrors.phone = 'Phone number is invalid';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length > 500) {
      newErrors.message = 'Message cannot exceed 500 characters';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      document.getElementsByName(firstErrorKey)?.[0]?.scrollIntoView({ behavior: 'smooth' });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const sanitizedData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
      };

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sanitizedData),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          const errorMap = {};
          result.errors.forEach((err) => {
            errorMap[err.field] = err.message;
          });
          setErrors(errorMap);

          result.errors.forEach((err) => {
            toast.error(`${err.field}: ${err.message}`);
          });
        } else {
          toast.error(result.message || 'Something went wrong');
        }

        setLoading(false);
        return;
      }

      toast.success('Thank you for your interest! We will contact you shortly.');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        country: '',
        countryCode: '',
        phone: '',
        message: '',
      });
      setErrors({});
    } catch (error) {
      console.error('Submission error:', error.message);
      toast.error(error.message || 'There was a problem submitting your request.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField
          label="First name"
          name="firstName"
          placeholder="First name"
          value={formData.firstName}
          onChange={handleChange}
          error={errors.firstName}
          variant="demo"
          required
        />
        <InputField
          label="Last name"
          name="lastName"
          placeholder="Last name"
          value={formData.lastName}
          onChange={handleChange}
          error={errors.lastName}
          variant="demo"
          required
        />
      </div>

      <InputField
        label="Email address"
        name="email"
        type="email"
        placeholder="you@company.com"
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        variant="demo"
        required
      />

      <InputField
        label="Company name"
        name="company"
        placeholder="Company name"
        value={formData.company}
        onChange={handleChange}
        error={errors.company}
        variant="demo"
        required
      />

      <div>
        <label htmlFor="country" className="block text-sm font-medium text-[#121725] mb-1">
          Country
        </label>
        <select
          id="country"
          name="country"
          value={formData.country}
          onChange={handleChange}
          className={`w-full h-11 px-3.5 py-2.5 bg-white border rounded-lg shadow-[0px_1px_2px_rgba(16,24,40,0.05)] text-base ${
            errors.country ? 'border-red-500' : 'border-[#e9eaec]'
          }`}
        >
          <option value="">Select a country</option>
          {countries.map((countryName) => (
            <option key={countryName} value={countryName}>
              {countryName}
            </option>
          ))}
        </select>
        {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
      </div>

      <div>
        <label htmlFor="phoneNumber" className="block text-sm font-medium text-[#121725] mb-1">
          Phone number
        </label>
        <PhoneInput
          country={'tn'}
          excludeCountries={['il']}
          value={formData.phone}
          onChange={(value, country) =>
            setFormData((prev) => ({
              ...prev,
              phone: value.startsWith('+') ? value : `+${value}`,
              country: country.name,
              countryCode: `+${country.dialCode}`,
            }))
          }
          inputProps={{
            name: 'phone',
            required: true,
            autoFocus: false,
          }}
          inputClass="!w-full !h-11 !text-base !px-15"
          containerClass="!w-full"
        />
        {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
      </div>

      <div>
        <Textarea
          label="Message"
          name="message"
          placeholder="Leave us a message..."
          value={formData.message}
          onChange={handleChange}
          rows={4}
          maxLength={500}
          variant="demo"
          required
          error={errors.message}
        />
        <p className="text-sm text-gray-500 mt-1">{formData.message.length} / 500</p>
      </div>

      <div className="pt-4">
        <Button type="submit" variant="gold" size="large" className="w-full" disabled={loading}>
          {loading ? 'Sending...' : 'Send message'}
        </Button>
      </div>
    </form>
  );
};

export default DemoForm;
